import { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  dismissInstallBanner,
  installBannerServerVersion,
  installBannerVersion,
  isInstallDismissed,
  isIosSafariLike,
  isStandalone,
  subscribeInstallBanner,
} from '@/lib/ios';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Bottom-pinned "Add to Home Screen" banner shown only when:
 *   - The visitor is on iOS Safari (Chrome on iOS uses the same engine
 *     but presents a different share menu; we cover both).
 *   - The PWA isn't already installed (display-mode: standalone).
 *   - The visitor hasn't dismissed the banner before
 *     (`localStorage['mycity.ios-install']` === 'dismissed').
 *
 * Why iOS-specific: Android Chrome triggers `beforeinstallprompt`
 * automatically, so the install gesture is one-tap. iOS Safari has no
 * such API — the user must use the share sheet → "Add to Home Screen".
 * This banner walks them through it.
 *
 * Also iOS-specific: web push only works for PWAs installed to the
 * Home Screen on iOS 16.4+. Until the user installs, no push reaches
 * them (regardless of VAPID + permission state). This banner is the
 * unlock for that path.
 *
 * Dismissal stays permanent on purpose — but it is no longer a dead end:
 * Profile → Notifications can call `showInstallBanner()` to bring it back,
 * so the instructions are reachable from where a user looks for them.
 * Detection and the dismissal store live in `@/lib/ios`, shared with that
 * screen.
 */
export function IosInstallBanner(): React.JSX.Element | null {
  const { t } = useTranslation();
  const signedIn = useAuthStore((s) => s.status) === 'signed-in';
  // Re-render when the store bumps (dismissed here, or re-shown from
  // Profile). useSyncExternalStore is the canonical pattern; avoids React
  // Compiler's "setState in effect" warning.
  useSyncExternalStore(subscribeInstallBanner, installBannerVersion, installBannerServerVersion);
  const [hydrated, setHydrated] = useState(false);
  if (!hydrated && typeof window !== 'undefined') {
    queueMicrotask(() => setHydrated(true));
  }

  if (!hydrated) return null;
  if (!isIosSafariLike()) return null;
  if (isStandalone()) return null;
  if (isInstallDismissed()) return null;

  return (
    <div
      role="dialog"
      aria-label={t('ios.installTitle')}
      className={cn(
        'bg-card text-foreground border-border fixed inset-x-3 z-40 mx-auto flex max-w-md flex-col gap-2 rounded-2xl border p-4 shadow-lg',
        // Signed in → clear the docked bottom nav; signed out → hug the
        // bottom edge (plus the home-indicator inset either way).
        signedIn
          ? 'bottom-[calc(5rem+env(safe-area-inset-bottom))]'
          : 'bottom-[calc(0.75rem+env(safe-area-inset-bottom))]',
      )}
    >
      <div className="flex items-start gap-3">
        <Share2 className="text-primary mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="flex-1">
          <p className="text-sm font-semibold">{t('ios.installTitle')}</p>
          <p className="text-muted mt-1 text-xs leading-relaxed">{t('ios.installBody')}</p>
        </div>
        <button
          type="button"
          aria-label={t('common.dismiss')}
          onClick={dismissInstallBanner}
          className="text-muted hover:text-foreground -mt-1 rounded-full p-1 transition"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
